import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Feature, Map, View } from 'ol';
import { OSM } from 'ol/source';
import { MapService } from '../../../services/map.service';
import { defaults as defaultInteractions } from 'ol/interaction';
import { defaults as defaultControls } from 'ol/control';
import { transform } from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import { Subscription } from 'rxjs';
import { GoogleMap } from '@angular/google-maps';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { fromExtent } from 'ol/geom/Polygon';
import { Extent } from 'ol/extent';
import { Stroke, Style } from 'ol/style';
import BaseLayer from 'ol/layer/Base';

@Component({
  selector: 'ebw-map-preview',
  templateUrl: './map-preview.component.html',
  styleUrls: ['./map-preview.component.scss']
})
export class MapPreviewComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() previewZoomOverflow: number = 4;
  @Input() style: string = '';
  @Input() previewWidth: string = '250px';
  @Input() previewHeight: string = '150px';
  @Input() public isGoogleKeyEnabled: boolean;

  @ViewChild('googleMapPreview') googleMapsPreviewMap: GoogleMap = null;

  previewGoogleMapsOptions = {
    disableDefaultUI: true,
    zoomControl: false,
    streetViewControl: false,
    keyboardShortcuts: false,
    clickableIcons: false,
    disableDoubleClickZoom: true,
    gestureHandling: 'none',
    scrollwheel: false,
    panControl: false,
    rotateControl: false,
    fullscreenControl: false
  };

  previewMapReady: boolean = false;

  openLayersMapPreview: Map;

  subscriptions: Subscription[] = [];

  activeMap: number;

  noneBaseLayer: BaseLayer = new TileLayer({
    source: null
  });
  osmBaseLayer: BaseLayer = new TileLayer({
    source: new OSM()
  });
  extentLayer: VectorLayer<any> = null;
  extentStyle: Style = new Style({
    stroke: new Stroke({
      color: 'red',
      width: 4
    })
  });

  constructor(public mapService: MapService, private cdRef: ChangeDetectorRef, private zone: NgZone) {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  ngAfterViewInit(): void {
    if (this.isGoogleKeyEnabled) {
      this.mapService.readyGoogleMapsMapChanged.subscribe({
        next: (isReady) => {
          if (isReady) {
            this.googleMapsPreviewMap?.googleMap.setMapTypeId(this.mapService.googleMapsBasemapType);
            this.initializeSubscriptions();
          }
        },
        error: (err) => {
          console.log('Errore inizializzazione GoogleMaps', err);
        }
      });
    } else {
      this.initializeSubscriptions();
    }
  }

  ngOnInit(): void {
    this.activeMap = this.mapService.showingMap;
    this.initializeOpenLayersPreview();
  }

  /** Disable all interactions and controls from OpenLayers */
  private removeInteractionsAndControls() {
    this.openLayersMapPreview.getInteractions().forEach((interaction) => {
      interaction.setActive(false);
    });

    this.openLayersMapPreview.getControls().forEach((control) => {
      this.openLayersMapPreview.removeControl(control);
    });
  }

  initializeOpenLayersPreview() {
    let olMapCenter = transform(this.mapService.mapCenter, MapService.EPSG_4326_KEY, MapService.EPSG_3857_KEY);

    let extentFeature = this.createExtentFeature(this.mapService.mapExtent);
    this.extentLayer = new VectorLayer({
      source: new VectorSource({
        features: [extentFeature]
      }),
      visible: true,
      zIndex: 999
    });

    //Open Layers Map initialization
    this.openLayersMapPreview = new Map({
      target: 'ol-preview-map',

      view: new View({
        center: olMapCenter,
        zoom: this.mapService.mapZoom - this.previewZoomOverflow
      }),
      //The basemaps that were recently listed and loaded.
      layers: [this.noneBaseLayer, this.osmBaseLayer, this.extentLayer]
    });

    this.removeInteractionsAndControls();

    //Without this, the map is not shown.
    // Dinamically solves the map dimension.
    setTimeout(() => {
      this.openLayersMapPreview.updateSize();
    }, 0);
  }

  createExtentFeature(extent: Extent): Feature {
    const extentBox = new Feature({
      geometry: fromExtent(extent)
    });
    extentBox.setStyle(this.extentStyle);
    return extentBox;
  }

  private initializeSubscriptions() {
    this.subscriptions.push(
      this.mapService.mapCenterChanged.subscribe((center: number[]) => {
        let mapCenter = transform(center, MapService.EPSG_4326_KEY, MapService.EPSG_3857_KEY);
        this.openLayersMapPreview.getView().setCenter(mapCenter);
        this.googleMapsPreviewMap?.googleMap.setCenter({ lat: mapCenter[0], lng: mapCenter[1] });
      })
    );

    this.subscriptions.push(
      this.mapService.mapZoomChanged.subscribe((zoom: number) => {
        this.openLayersMapPreview.getView().setZoom(zoom - this.previewZoomOverflow);
        this.googleMapsPreviewMap?.googleMap.setZoom(zoom - this.previewZoomOverflow);
      })
    );

    this.subscriptions.push(
      this.mapService.googleMapsBasemapTypeChanged.subscribe((type: string) => {
        this.googleMapsPreviewMap?.googleMap.setMapTypeId(type);

        if (type == '') {
          //OSM basemap
          this.osmBaseLayer.setOpacity(1);
          this.noneBaseLayer.setOpacity(0);
        } else if (type == undefined) {
          //None basemap
          this.osmBaseLayer.setOpacity(0);
          this.noneBaseLayer.setOpacity(1);
        } else {
          //google basemap
          this.osmBaseLayer.setOpacity(0);
          this.noneBaseLayer.setOpacity(0);
        }
      })
    );

    this.subscriptions.push(
      this.mapService.mapExtentChanged.subscribe((extent: Extent) => {
        let extentFeature = this.createExtentFeature(extent);
        this.extentLayer.getSource().clear();
        this.extentLayer.getSource().addFeature(extentFeature);
      })
    );

    this.subscriptions.push(
      this.mapService.showingMapChanged.subscribe((showing) => {
        this.activeMap = showing;
      })
    );

    this.previewMapReady = true;
    this.cdRef.detectChanges();
  }
}
